import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Clock, FileXls, Check, X, MagnifyingGlass } from "@phosphor-icons/react";
import { MonthYearPicker, DatePicker } from "@/components/inputs";
import { useAuth } from "@/context/AuthContext";
import { AttendanceStatusPill } from "@/components/AttendanceStatusPill";

function formatDuration(mins) {
  if (mins == null || mins === "" || isNaN(Number(mins))) return "—";
  const n = Number(mins);
  if (n <= 0) return "0 menit";
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (h > 0 && m > 0) return `${h} jam ${m} menit`;
  if (h > 0) return `${h} jam`;
  return `${m} menit`;
}
function typeLabel(t) {
  const m = { sakit: "Sakit", cuti: "Cuti", izin: "Izin", terlambat: "Datang Terlambat", pulang_cepat: "Pulang Cepat" };
  return m[t] || t;
}

export default function AttendanceAdminPage() {
  const wibStr = new Date().toLocaleDateString("en-GB", { timeZone: "Asia/Jakarta" });
  const [wibD, wibM, wibY] = wibStr.split("/").map(Number);
  const today = `${wibY}-${String(wibM).padStart(2, "0")}-${String(wibD).padStart(2, "0")}`;
  const [month, setMonth] = useState(wibM);
  const [year, setYear] = useState(wibY);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const { user } = useAuth();
  const isPrimaryAdmin = user?.email?.toLowerCase() === "general@samitragroup.com";
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [employees, setEmployees] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");
  const [records, setRecords] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [detail, setDetail] = useState(null);
  const [leaveAction, setLeaveAction] = useState(null);
  const [corrAction, setCorrAction] = useState(null);
  const [busy, setBusy] = useState(false);

  const toISO = (ddmmyyyy) => {
    if (!ddmmyyyy) return "";
    const [d,m,y] = ddmmyyyy.split("/").map(Number);
    if (!d||!m||!y) return "";
    return `${String(y).padStart(4,"0")}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  };

  const loadEmployees = () => api.get(`/employees?month=${month}&year=${year}`).then((r) => setEmployees(r.data)).catch(() => {});
  const loadRecords = () => {
    let q = "";
    const from = toISO(dateFrom);
    const to = toISO(dateTo);
    q = from && to ? `date_from=${from}&date_to=${to}` : `date_from=${today}&date_to=${today}`;
    if (selectedUser !== "all") q += `&user_id=${selectedUser}`;
    if (status !== "all") q += `&status=${status}`;
    api.get(`/attendance?${q}`).then((r) => setRecords(r.data)).catch(() => {});
  };
  const loadLeaves = () => api.get("/attendance/leave-requests").then((r) => setLeaves(r.data)).catch(() => {});
  const loadCorr = () => api.get("/attendance/corrections").then((r) => setCorrections(r.data)).catch(() => {});

  useEffect(() => { loadEmployees(); loadRecords(); }, [month, year, status, selectedUser, dateFrom, dateTo]);
  useEffect(() => { loadLeaves(); loadCorr(); }, []);

  const exportExcel = async () => {
    setBusy(true);
    try {
      const from = toISO(dateFrom);
      const to = toISO(dateTo);
      const q = from && to ? `date_from=${from}&date_to=${to}` : `month=${month}&year=${year}`;
      const res = await api.get(`/attendance/reports/excel?${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = from && to ? `absensi-${from}-sampai-${to}.xlsx` : `absensi-${month}-${year}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("Excel diunduh");
    } catch (e) { toast.error("Gagal ekspor"); } finally { setBusy(false); }
  };

  const filtered = records.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return r.employee_name.toLowerCase().includes(s) || r.date.includes(s) || r.status.includes(s) || (r.check_in?.address && r.check_in.address.toLowerCase().includes(s)) || (r.check_out?.address && r.check_out.address.toLowerCase().includes(s));
  });

  // Kedua tabel pengajuan tidak terfilter tanggal, hanya bulan (sesuai permintaan)
  const monthPrefix = `${year}-${String(month).padStart(2,"0")}`;
  const monthStart = `${monthPrefix}-01`;
  const monthEnd = `${monthPrefix}-${String(new Date(year, month, 0).getDate()).padStart(2,"0")}`;
  const leavesByMonth = leaves.filter((l) => l.end_date >= monthStart && l.start_date <= monthEnd);
  const corrByMonth = corrections.filter((c) => c.date && c.date.startsWith(monthPrefix));
  const pendingLeaves = leavesByMonth.filter((l) => l.status === "pending");
  const pendingCorr = corrByMonth.filter((c) => c.status === "pending");

  return (
    <AppLayout title="Absensi Karyawan">
      <div className="max-w-6xl space-y-6 animate-fade-up">
        <Card className="p-6 space-y-4">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Tanggal Mulai</Label>
              <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="Mulai tanggal" testId="admin-att-date-from" />
            </div>
            <div className="space-y-1.5">
              <Label>Tanggal Sampai</Label>
              <DatePicker value={dateTo} onChange={setDateTo} placeholder="Sampai tanggal" testId="admin-att-date-to" />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="min-w-0 space-y-1.5">
              <Label>Karyawan</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="w-full" data-testid="admin-att-employee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0 space-y-1.5">
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-full" data-testid="admin-att-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua</SelectItem>
                  <SelectItem value="hadir">Hadir</SelectItem>
                  <SelectItem value="izin">Izin</SelectItem>
                  <SelectItem value="sakit">Sakit</SelectItem>
                  <SelectItem value="cuti">Cuti</SelectItem>
                  <SelectItem value="terlambat">Terlambat</SelectItem>
                  <SelectItem value="pulang_cepat">Pulang Cepat</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-sm">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Cari nama/tanggal/status…" value={search} onChange={(e)=>setSearch(e.target.value)} className="pl-9" data-testid="admin-att-search" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" className="gap-2" onClick={exportExcel} disabled={busy} data-testid="admin-att-export"><FileXls size={18}/> Ekspor Excel</Button>
              {(dateFrom || dateTo) && <Button variant="destructive" onClick={()=>{setDateFrom(""); setDateTo("");}} className="h-10 gap-1.5 px-4" data-testid="clear-date-filter"><X size={16} weight="bold"/> Hapus</Button>}
            </div>
          </div>
        </Card>

        <Card className="overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Nama</TableHead><TableHead>Status</TableHead><TableHead>Masuk</TableHead><TableHead>Pulang</TableHead><TableHead>Durasi</TableHead><TableHead>Detail</TableHead></TableRow></TableHeader>
            <TableBody>
              {filtered.length===0 ? <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Tidak ada data.</TableCell></TableRow> : filtered.map((r)=>(
                <TableRow key={r.id} data-testid={`admin-att-row-${r.date}-${r.user_id}`}>
                  <TableCell>{r.date}</TableCell>
                  <TableCell>{r.employee_name}</TableCell>
                  <TableCell><AttendanceStatusPill status={r.status} /></TableCell>
                  <TableCell>{r.check_in?.time || "—"}</TableCell>
                  <TableCell>{r.check_out?.time || "—"}</TableCell>
                  <TableCell>{formatDuration(r.duration_minutes)}</TableCell>
                  <TableCell><Button variant="ghost" size="sm" onClick={()=>setDetail(r)} data-testid="view-att-detail">Lihat</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <section className="space-y-4" aria-labelledby="attendance-requests-heading">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-1">
              <h2 id="attendance-requests-heading" className="font-heading text-lg font-bold">Pengajuan & Koreksi Absensi</h2>
              <p className="text-sm text-muted-foreground">Pilih periode untuk melihat pengajuan sakit/cuti, terlambat/pulang cepat, dan koreksi absensi.</p>
            </div>
            <div className="w-full shrink-0 space-y-1.5 sm:w-72">
              <Label>Periode Pengajuan</Label>
              <MonthYearPicker month={month} year={year} onChange={(m,y)=>{setMonth(m); setYear(y);}} years={[year-2,year-1,year,year+1]} monthTestId="admin-att-month" yearTestId="admin-att-year" className="w-full" />
            </div>
          </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6 space-y-3">
            <h3 className="font-heading font-bold flex items-center gap-2"><Clock size={18}/> Pengajuan Sakit/Cuti • Terlambat/Pulang {pendingLeaves.length>0 && <Badge>{pendingLeaves.length} pending</Badge>}</h3>
            {leavesByMonth.length===0 ? <p className="text-sm text-muted-foreground">Belum ada pengajuan.</p> : leavesByMonth.slice(0,20).map((l)=>(
              <div key={l.id} className="rounded-lg border border-border p-3 space-y-2" data-testid={`admin-leave-${l.id}`}>
                <div className="flex items-center justify-between"><span className="font-medium">{l.employee_name}</span><Badge variant="outline" className={l.status==="pending"?"border-[hsl(var(--warning))] text-[hsl(var(--warning))]": l.status==="approved"?"border-[hsl(var(--success))] text-[hsl(var(--success))]":"border-destructive text-destructive"}>{l.status}</Badge></div>
                <p className="text-sm capitalize">{typeLabel(l.type)} · {l.start_date} → {l.end_date}</p>
                <p className="text-xs text-muted-foreground truncate">{l.reason||"—"}</p>
                {l.proof_path && <a href={fileUrl(l.proof_path)} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Lihat bukti</a>}
                {l.status==="pending" && <div className="flex gap-2"><Button size="sm" variant="success" className="gap-1" onClick={()=>setLeaveAction({item:l, action:"approved"})} data-testid={`approve-leave-${l.id}`}><Check size={14}/> Setujui</Button><Button size="sm" variant="destructive" className="gap-1" onClick={()=>setLeaveAction({item:l, action:"rejected"})} data-testid={`reject-leave-${l.id}`}><X size={14}/> Tolak</Button></div>}
                {l.admin_note && <p className="text-xs">Catatan admin: {l.admin_note}</p>}
              </div>
            ))}
          </Card>

          <Card className="p-6 space-y-3">
            <h3 className="font-heading font-bold flex items-center gap-2"><Clock size={18}/> Koreksi Absensi {pendingCorr.length>0 && <Badge>{pendingCorr.length} pending</Badge>}</h3>
            {corrByMonth.length===0 ? <p className="text-sm text-muted-foreground">Belum ada koreksi.</p> : corrByMonth.slice(0,20).map((c)=>(
              <div key={c.id} className="rounded-lg border border-border p-3 space-y-2" data-testid={`admin-corr-${c.id}`}>
                <div className="flex items-center justify-between"><span className="font-medium">{c.employee_name} · {c.date}</span><Badge variant="outline" className={c.status==="approved"?"border-[hsl(var(--success))] text-[hsl(var(--success))]": c.status==="rejected"?"border-destructive text-destructive":"border-[hsl(var(--warning))] text-[hsl(var(--warning))]"}>{c.status}</Badge></div>
                <p className="text-xs truncate">{c.reason}</p>
                <p className="text-xs text-muted-foreground">{[c.proposed_check_in && `In: ${c.proposed_check_in}`, c.proposed_check_out && `Out: ${c.proposed_check_out}`, c.proposed_status && `Status: ${c.proposed_status}`].filter(Boolean).join(" · ") || "—"}</p>
                {c.proof_path && <a href={fileUrl(c.proof_path)} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Lihat bukti</a>}
                {c.status==="pending" && <div className="flex gap-2"><Button size="sm" variant="success" onClick={()=>setCorrAction({item:c, mode:"approve"})} data-testid={`approve-corr-${c.id}`}>Setujui & Perbaiki</Button><Button size="sm" variant="destructive" onClick={()=>setCorrAction({item:c, mode:"reject"})} data-testid={`reject-corr-${c.id}`}>Tolak</Button></div>}
                {c.admin_note && <p className="text-xs">Catatan: {c.admin_note}</p>}
              </div>
            ))}
          </Card>
        </div>
        </section>
      </div>

      <Dialog open={!!detail} onOpenChange={(v)=>!v && setDetail(null)}>
        <DialogContent className="max-w-[480px] max-h-[82vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-base">Detail Absensi {detail?.employee_name} · {detail?.date}</DialogTitle></DialogHeader>
          {detail && (
            <div className="grid grid-cols-2 gap-3">
              <div><p className="text-xs text-muted-foreground">Status</p><AttendanceStatusPill status={detail.status} className="text-xs" /></div>
              <div><p className="text-xs text-muted-foreground">Durasi</p><p className="text-sm">{formatDuration(detail.duration_minutes)}</p></div>
              <div className="col-span-2"><p className="text-xs text-muted-foreground">Check-in</p><p className="text-sm">{detail.check_in?.time || "—"} {detail.check_in?.lat ? `(${detail.check_in.lat.toFixed(5)}, ${detail.check_in.lng.toFixed(5)})`:""}</p>{detail.check_in?.address && <p className="text-xs bg-muted rounded p-1.5 mt-1 line-clamp-2">{detail.check_in.address}</p>}{detail.check_in?.lat && <a href={`https://www.google.com/maps/search/?api=1&query=${detail.check_in.lat},${detail.check_in.lng}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Lihat di Google Maps</a>}{detail.check_in?.photo_path && <a href={fileUrl(detail.check_in.photo_path)} target="_blank" rel="noreferrer" className="block mt-2 rounded-lg border overflow-hidden bg-muted hover:opacity-90 transition-opacity"><img src={fileUrl(detail.check_in.photo_path)} alt="Foto check-in" className="w-full max-h-[180px] md:max-h-[200px] object-contain mx-auto" loading="lazy" /><p className="text-[11px] text-center text-muted-foreground py-1 bg-background">Klik untuk perbesar</p></a>}</div>
              <div className="col-span-2"><p className="text-xs text-muted-foreground">Check-out</p><p className="text-sm">{detail.check_out?.time || "—"} {detail.check_out?.lat ? `(${detail.check_out.lat.toFixed(5)}, ${detail.check_out.lng.toFixed(5)})`:""}</p>{detail.check_out?.address && <p className="text-xs bg-muted rounded p-1.5 mt-1 line-clamp-2">{detail.check_out.address}</p>}{detail.check_out?.lat && <a href={`https://www.google.com/maps/search/?api=1&query=${detail.check_out.lat},${detail.check_out.lng}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Lihat di Google Maps</a>}{detail.check_out?.photo_path && <a href={fileUrl(detail.check_out.photo_path)} target="_blank" rel="noreferrer" className="block mt-2 rounded-lg border overflow-hidden bg-muted hover:opacity-90 transition-opacity"><img src={fileUrl(detail.check_out.photo_path)} alt="Foto check-out" className="w-full max-h-[180px] md:max-h-[200px] object-contain mx-auto" loading="lazy" /><p className="text-[11px] text-center text-muted-foreground py-1 bg-background">Klik untuk perbesar</p></a>}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <LeaveReviewDialog action={leaveAction} setAction={setLeaveAction} onDone={()=>{loadLeaves(); loadRecords();}} />
      <CorrectionReviewDialog action={corrAction} setAction={setCorrAction} onDone={()=>{loadCorr(); loadRecords();}} />
    </AppLayout>
  );
}

function LeaveReviewDialog({ action, setAction, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!action) return;
    setSaving(true);
    try {
      await api.put(`/attendance/leave-requests/${action.item.id}`, { status: action.action, admin_note: note });
      toast.success(action.action==="approved"?"Disetujui":"Ditolak");
      setAction(null); setNote(""); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!action} onOpenChange={(v)=>!v && setAction(null)}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{action?.action==="approved"?"Setujui":"Tolak"} {typeLabel(action?.item.type)} · {action?.item.employee_name}</DialogTitle></DialogHeader>
        <div className="space-y-2"><Label>Catatan Admin</Label><Textarea value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Opsional" data-testid="leave-admin-note" /></div>
        <DialogFooter><Button variant="outline" onClick={()=>setAction(null)}>Batal</Button><Button variant={action?.action === "approved" ? "success" : "destructive"} onClick={submit} disabled={saving} data-testid="confirm-leave-review">{saving?"Menyimpan…":"Konfirmasi"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CorrectionReviewDialog({ action, setAction, onDone }) {
  const [note, setNote] = useState("");
  const [inTime, setInTime] = useState("");
  const [outTime, setOutTime] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (action?.item) {
      setInTime(action.item.proposed_check_in || "");
      setOutTime(action.item.proposed_check_out || "");
      setStatus(action.item.proposed_status || "");
      setNote("");
    }
  }, [action]);
  const submit = async (approved) => {
    setSaving(true);
    try {
      await api.put(`/attendance/corrections/${action.item.id}`, {
        status: approved ? "approved" : "rejected",
        admin_note: note,
        approved_check_in: approved ? (inTime || null) : null,
        approved_check_out: approved ? (outTime || null) : null,
        approved_status: approved ? (status || null) : null,
      });
      toast.success(approved ? "Koreksi disetujui & diterapkan" : "Koreksi ditolak");
      setAction(null); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setSaving(false); }
  };
  if (!action) return null;
  const isReject = action.mode==="reject";
  return (
    <Dialog open={!!action} onOpenChange={(v)=>!v && setAction(null)}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{isReject ? "Tolak" : "Setujui"} Koreksi {action.item.employee_name} · {action.item.date}</DialogTitle></DialogHeader>
        {!isReject && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Jam Masuk (HH:MM)</Label><Input value={inTime} onChange={(e)=>setInTime(e.target.value)} placeholder="08:30" data-testid="corr-approved-in" /></div>
              <div className="space-y-1.5"><Label>Jam Pulang (HH:MM)</Label><Input value={outTime} onChange={(e)=>setOutTime(e.target.value)} placeholder="17:00" data-testid="corr-approved-out" /></div>
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={status || "none"} onValueChange={(v)=>setStatus(v==="none"?"":v)}>
                <SelectTrigger data-testid="corr-approved-status"><SelectValue placeholder="Tidak diubah" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Tidak diubah</SelectItem><SelectItem value="hadir">Hadir</SelectItem><SelectItem value="sakit">Sakit</SelectItem><SelectItem value="cuti">Cuti</SelectItem><SelectItem value="terlambat">Terlambat</SelectItem><SelectItem value="pulang_cepat">Pulang Cepat</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        )}
        <div className="space-y-1.5"><Label>Catatan Admin</Label><Textarea value={note} onChange={(e)=>setNote(e.target.value)} data-testid="corr-admin-note" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setAction(null)}>Batal</Button>
          {isReject ? <Button variant="destructive" onClick={()=>submit(false)} disabled={saving} data-testid="confirm-corr-reject">Tolak</Button> : <Button variant="success" onClick={()=>submit(true)} disabled={saving} data-testid="confirm-corr-approve">Setujui</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
